import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import { TreeView, TreeItem } from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { GroupEntity } from '@backstage/catalog-model';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  root: { display: 'flex', flexDirection: 'column', gap: theme.spacing(1) },
  treeContainer: {
    maxHeight: 320,
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
  },
  breadcrumb: {
    padding: theme.spacing(0.5, 1),
    background: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
  },
  compactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.75),
  },
}));

interface TreeNode {
  id: string;
  title: string;
  children: TreeNode[];
}

/**
 * Sorts sibling nodes alphabetically by display title. Case- and
 * accent-insensitive (`sensitivity: 'base'`) so "acme" and "Acme" sort
 * next to each other rather than by code point; falls back to the machine
 * ID when titles are equal so ordering stays deterministic.
 */
function compareNodes(a: TreeNode, b: TreeNode): number {
  const byTitle = a.title.localeCompare(b.title, undefined, {
    sensitivity: 'base',
    numeric: true,
  });
  return byTitle !== 0 ? byTitle : a.id.localeCompare(b.id);
}

function buildTree(groups: GroupEntity[]): TreeNode[] {
  const byId = new Map<string, TreeNode>(
    groups.map(g => [
      g.metadata.name,
      { id: g.metadata.name, title: g.metadata.title ?? g.metadata.name, children: [] },
    ]),
  );

  const roots: TreeNode[] = [];
  for (const g of groups) {
    const node = byId.get(g.metadata.name)!;
    const parentId = g.spec?.parent as string | undefined;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort siblings at every depth. Iterating the flat `byId` map covers all
  // nodes (including any unreachable from `roots`) without recursion, so
  // `spec.parent` cycles in bad catalog data cannot cause infinite descent.
  for (const node of byId.values()) {
    node.children.sort(compareNodes);
  }
  roots.sort(compareNodes);

  return roots;
}

function getLineage(
  machineId: string,
  groups: GroupEntity[],
): GroupEntity[] {
  const byId = new Map(groups.map(g => [g.metadata.name, g]));
  const lineage: GroupEntity[] = [];
  let current = byId.get(machineId);
  const visited = new Set<string>();
  while (current && !visited.has(current.metadata.name)) {
    visited.add(current.metadata.name);
    lineage.unshift(current);
    const parentId = current.spec?.parent as string | undefined;
    current = parentId ? byId.get(parentId) : undefined;
  }
  return lineage;
}

function filterTree(nodes: TreeNode[], query: string): TreeNode[] {
  if (!query) return nodes;
  const q = query.toLowerCase();
  return nodes
    .map(node => {
      const filteredChildren = filterTree(node.children, query);
      if (
        node.title.toLowerCase().includes(q) ||
        node.id.toLowerCase().includes(q) ||
        filteredChildren.length > 0
      ) {
        return { ...node, children: filteredChildren };
      }
      return null;
    })
    .filter((n): n is TreeNode => n !== null);
}

function RenderTree({
  nodes,
  onSelect,
  selectedId,
}: {
  nodes: TreeNode[];
  onSelect: (id: string) => void;
  selectedId: string;
}) {
  return (
    <>
      {nodes.map(node => (
        <TreeItem
          key={node.id}
          nodeId={node.id}
          label={
            <span
              role="button"
              tabIndex={0}
              onClick={() => onSelect(node.id)}
              onKeyDown={e => e.key === 'Enter' && onSelect(node.id)}
            >
              {node.title}
            </span>
          }
        >
          {node.children.length > 0 && (
            <RenderTree
              nodes={node.children}
              onSelect={onSelect}
              selectedId={selectedId}
            />
          )}
        </TreeItem>
      ))}
    </>
  );
}

export interface ParentGroupPickerProps {
  value: string; // machine ID of selected parent
  onChange: (machineId: string) => void;
  /** Machine ID of the team being edited — prevents self-selection (FR-022) */
  excludeMachineId?: string;
  showParentChangeWarning?: boolean;
  /** Compact mode: shows breadcrumb + "Change…" button; full picker opens in a dialog */
  compact?: boolean;
}

export function ParentGroupPicker({
  value,
  onChange,
  excludeMachineId,
  showParentChangeWarning = false,
  compact = false,
}: ParentGroupPickerProps) {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [groups, setGroups] = useState<GroupEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [parentChanged, setParentChanged] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(value);

  useEffect(() => {
    catalogApi
      .getEntities({
        filter: { kind: 'Group' },
        fields: [
          'metadata.name',
          'metadata.title',
          'metadata.annotations',
          'spec.parent',
          'spec.type',
        ],
      })
      .then(res => {
        // Exclude archived groups and the team itself (FR-022). Team-type
        // nodes are deliberately kept selectable — nesting depth is
        // unconstrained, a team is a legitimate parent choice.
        const active = (res.items as GroupEntity[]).filter(
          g =>
            g.metadata.annotations?.['backstage.io/lifecycle'] !== 'deprecated' &&
            g.metadata.name !== excludeMachineId,
        );
        setGroups(active);
      })
      .finally(() => setLoading(false));
  }, [catalogApi, excludeMachineId]);

  const tree = useMemo(() => buildTree(groups), [groups]);
  const filtered = useMemo(() => filterTree(tree, search), [tree, search]);

  const lineage = useMemo(
    () => (value ? getLineage(value, groups) : []),
    [value, groups],
  );

  const handleSelect = (machineId: string) => {
    if (value && value !== machineId) {
      setParentChanged(true);
    }
    onChange(machineId);
  };

  if (loading) return <CircularProgress size={20} />;

  const breadcrumbText = lineage.map(g => g.metadata.title ?? g.metadata.name).join(' > ');

  // Compact mode: read-only breadcrumb + "Change parent…" dialog trigger
  if (compact) {
    const pendingLineage = pendingSelection ? getLineage(pendingSelection, groups) : [];

    return (
      <Box>
        <InputLabel shrink>Parent Group</InputLabel>
        <Box className={classes.compactRow}>
          <Breadcrumbs maxItems={4} itemsAfterCollapse={2} itemsBeforeCollapse={1} style={{ flex: 1 }}>
            {lineage.map(g => (
              <Typography key={g.metadata.name} variant="body2" color="textPrimary">
                {g.metadata.title ?? g.metadata.name}
              </Typography>
            ))}
          </Breadcrumbs>
          <Button size="small" variant="text" color="primary" onClick={() => { setPendingSelection(value); setDialogOpen(true); }}>
            Change…
          </Button>
        </Box>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Change Parent Group</DialogTitle>
          <DialogContent>
            <Box mb={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search groups…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                variant="outlined"
              />
            </Box>
            <Box className={classes.treeContainer}>
              <TreeView
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
                selected={pendingSelection}
              >
                <RenderTree
                  nodes={filtered}
                  onSelect={setPendingSelection}
                  selectedId={pendingSelection}
                />
              </TreeView>
            </Box>
            {pendingLineage.length > 0 && (
              <Box mt={1}>
                <Typography variant="caption" color="textSecondary">Selected: </Typography>
                <Breadcrumbs maxItems={5} style={{ display: 'inline-flex' }}>
                  {pendingLineage.map(g => (
                    <Typography key={g.metadata.name} variant="caption" color="textPrimary">
                      {g.metadata.title ?? g.metadata.name}
                    </Typography>
                  ))}
                </Breadcrumbs>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                setDialogOpen(false);
                setSearch('');
                handleSelect(pendingSelection);
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Full inline mode (used in Scaffolder create flow)
  return (
    <Box className={classes.root}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search groups…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        variant="outlined"
      />

      <Box className={classes.treeContainer}>
        <TreeView
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          selected={value}
        >
          <RenderTree
            nodes={filtered}
            onSelect={handleSelect}
            selectedId={value}
          />
        </TreeView>
      </Box>

      {breadcrumbText && (
        <Box className={classes.breadcrumb}>
          <Typography variant="caption">{breadcrumbText}</Typography>
        </Box>
      )}

      {showParentChangeWarning && parentChanged && (
        <Chip
          label="Moving this team will affect organisational reporting and permissions"
          color="default"
          variant="outlined"
          size="small"
        />
      )}
    </Box>
  );
}
